import parser from 'html-react-parser';
import React, { useEffect, useRef, useState } from 'react';
import { IoChevronBack, IoChevronForward } from 'react-icons/io5';
import Slider from 'react-slick';
import useIntervalTransition from '../../../../../../hooks/useIntervalTransition';
import { dealStatus, dealTypes } from '../../../../../login/utils/Status';
import { commaSeperator } from '../../../../../utils/validation';
import { Deals } from '../../../../deals/redux/types';
import { createInterestRate, createMinInvestment, getType, isShowFields } from '../../../utils';

type Props = {
    list: Deals[];
};
export const titleClass = 'text-[#27AE60] font-[WorkSans] font-normal text-xs text_wrap';
export const valueClass = 'text-[#142C53] text-sm font-[WorkSans-Semibold] font-semibold';

function SampleNextArrow(props: any) {
    const { className, style, onClick } = props;
    return (
        <div className={className} style={style} onClick={onClick}>
            <IoChevronForward size={'1rem'} color="#fff" />
        </div>
    );
}

function SamplePrevArrow(props: any) {
    const { className, style, onClick } = props;
    return (
        <div className={className} style={style} onClick={onClick}>
            <IoChevronBack size={'1rem'} color="#fff" />
        </div>
    );
}

const settings = {
    dots: false,
    infinite: false,
    slidesToShow: 3.12,
    slidesToScroll: 1,
    nextArrow: <SampleNextArrow />,
    prevArrow: <SamplePrevArrow />,
};

const RecentlyClosedDesktop: React.FC<Props> = ({ list }) => {
    const currentIndex = useIntervalTransition(list.length, 100);
    const [maxHeight, setMaxHeight] = useState(0);

    const cardRefs = useRef<(HTMLDivElement | null)[]>([]);
    useEffect(() => {
        const updateHeight = () => {
            const heights = cardRefs.current.map((card) => card?.offsetHeight || 0);
            setMaxHeight(Math.max(...heights));
        };

        updateHeight();

        window.addEventListener('resize', updateHeight);
        return () => window.removeEventListener('resize', updateHeight);
    }, [list]);

    return (
        <div className="slider-container recently-closed-slider-container">
            <Slider {...settings}>
                {list.map((d, index) => {
                    const isStatus = d.deal.status === dealStatus.upcoming || d.deal.status === dealStatus.completed;
                    return (
                        <div
                            key={`${d.deal.uuid}_${index}`}
                            ref={(el) => (cardRefs.current[index] = el)}
                            className={`pr-6 `}
                        >
                            <div
                                style={{
                                    opacity: index <= currentIndex ? 1 : 0,
                                    transform: `scale(${index <= currentIndex ? 1 : 0.8})`,
                                    transition: 'opacity 0.3s ease-in-out, transform 0.3s ease-in-out',
                                    height: maxHeight > 0 ? `${maxHeight}px` : 'auto',
                                }}
                                className={`${
                                    isStatus ? '' : ' hover:shadow-card cursor-pointer'
                                } border border-white relative bg-white `}
                            >
                                {/** Image and deal type  */}
                                <div className="p-2">
                                    <div
                                        className={`h-[120px] bg-cover bg-no-repeat bg-white flex items-start`}
                                        style={d.deal.image ? { backgroundImage: `url(${d.deal.image})` } : {}}
                                    >
                                        <div className="flex justify-start pr-4 pt-4 relative right-2">
                                            {getType(d.deal.info.summary?.dealType) !== '' && (
                                                <label className="bg-white w-auto px-2 py-1 text-[#142C53] font-[WorkSans-Semibold] font-bold text-xs">
                                                    {getType(d.deal.info.summary?.dealType)}
                                                </label>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                {/** End Image and deal type  */}

                                {/** Detail container */}
                                <div className={`px-6 py-2 h-[calc(100%-136px)] bg-white`}>
                                    <div className="flex flex-col justify-between h-full">
                                        <div className="min-h-[16rem]">
                                            {/** Category and Country */}
                                            <p className="text-xs font-[WorkSans] font-bold lg:font-normal line_truc min-h-[2rem]">
                                                <span className="text-[#6D707B]">{d.issuer?.category}</span>
                                                {d.issuer?.country && (
                                                    <span className="text-[#6D707B]"> | {d.issuer.country}</span>
                                                )}
                                            </p>
                                            {/** End Category and Country */}

                                            {/** Deal Name */}
                                            <p className="text-[#142C53] uppercase whitespace-nowrap block text-ellipsis overflow-hidden font-semibold font-[WorkSans-Semibold] text-2xl my-1">
                                                {d.deal.info.summary?.title}
                                            </p>
                                            {/**End Deal Name */}

                                            {/** Deal Description */}
                                            <p className="text-[#020F50] font-[Lato] text-xs  line_truc min-h-[3rem]">
                                                {d.deal.info.summary?.description}
                                            </p>
                                            {/** End Deal Description */}

                                            <div className="h-2" />
                                            {/** Info */}
                                            <div className="grid grid-cols-2 gap-2">
                                                {/**Industry */}
                                                <div>
                                                    <p className={titleClass}>Industry</p>
                                                    <p className={valueClass}>{d.issuer?.category}</p>
                                                </div>

                                                {/** Asset Class Or Instrument Type */}
                                                <div>
                                                    <p className={titleClass}>
                                                        {dealTypes.fund === d.deal.info.summary?.dealType
                                                            ? 'Asset Class'
                                                            : 'Instrument Type'}
                                                    </p>
                                                    <p className={valueClass}>
                                                        {d.deal.info.summary?.instrumentType || '-'}
                                                    </p>
                                                </div>

                                                {/** Target Funding Size */}
                                                <div>
                                                    <p className={titleClass}>Target Funding Size</p>
                                                    <p className={valueClass}>
                                                        {d.deal.info.summary?.targetFundingSize
                                                            ? `${
                                                                  d.deal.info.summary.targetFundingSize.currency
                                                              } ${commaSeperator(
                                                                  String(
                                                                      d.deal.info.summary.targetFundingSize
                                                                          .amountUpto2dp || 0,
                                                                  ),
                                                              )}`
                                                            : 'NA'}
                                                    </p>
                                                </div>

                                                {/** Target Returns */}
                                                <div>
                                                    <p className={titleClass}>Target Returns</p>
                                                    <p className={valueClass}>{parser(createInterestRate(d.deal))}</p>
                                                </div>

                                                {/**Fund Manager */}
                                                {dealTypes.fund === d.deal.info.summary?.dealType &&
                                                    (isShowFields(d.deal.status) ||
                                                        dealStatus.upcoming === d.deal.status) && (
                                                        <div>
                                                            <p className={titleClass}>Fund Manager</p>
                                                            <p className={valueClass}>{d.issuer?.name || '-'}</p>
                                                        </div>
                                                    )}
                                                {/**Tenor */}
                                                {dealTypes.debt === d.deal.info.summary?.dealType &&
                                                    (isShowFields(d.deal.status) ||
                                                        dealStatus.upcoming === d.deal.status) && (
                                                        <div>
                                                            <p className={titleClass}>Tenor</p>
                                                            <p className={valueClass}>
                                                                {d.deal.info.summary.terms?.tenorInMonths
                                                                    ? `${d.deal.info.summary.terms?.tenorInMonths} months`
                                                                    : '-'}
                                                            </p>
                                                        </div>
                                                    )}
                                                {/**Distribution */}
                                                {(dealTypes.debt === d.deal.info.summary?.dealType ||
                                                    dealTypes.fund === d.deal.info.summary?.dealType) &&
                                                    isShowFields(d.deal.status) && (
                                                        <div>
                                                            <p className={titleClass}>Distribution</p>
                                                            <p className={valueClass}>
                                                                {d.deal.info.summary.terms?.distribution || '-'}
                                                            </p>
                                                        </div>
                                                    )}
                                                {/**Equity Funding To Date */}
                                                {dealTypes.equity === d.deal.info.summary?.dealType &&
                                                    isShowFields(d.deal.status) && (
                                                        <div>
                                                            <p className={titleClass}>Equity Funding To Date</p>
                                                            <p className={valueClass}>
                                                                {d.deal.info.summary.terms?.equityFundingToDate
                                                                    ?.amountUpto2dp
                                                                    ? `${
                                                                          d.deal.info.summary.terms.equityFundingToDate
                                                                              .currency
                                                                      } ${commaSeperator(
                                                                          String(
                                                                              d.deal.info.summary.terms
                                                                                  .equityFundingToDate.amountUpto2dp,
                                                                          ),
                                                                      )}`
                                                                    : '-'}
                                                            </p>
                                                        </div>
                                                    )}
                                                {/**Funding Round */}
                                                {dealTypes.equity === d.deal.info.summary?.dealType &&
                                                    isShowFields(d.deal.status) && (
                                                        <div>
                                                            <p className={titleClass}>Funding Round</p>
                                                            <p className={valueClass}>
                                                                {d.deal.info.summary.terms?.fundingRound || '-'}
                                                            </p>
                                                        </div>
                                                    )}
                                                {/** Target Return (p.a.) Or Interest Rate (p.a.)*/}
                                                {(dealTypes.debt === d.deal.info.summary?.dealType ||
                                                    dealTypes.fund === d.deal.info.summary?.dealType) &&
                                                    (isShowFields(d.deal.status) ||
                                                        d.deal.status === dealStatus.upcoming) && (
                                                        <div>
                                                            <p className={titleClass}>
                                                                {dealTypes.fund === d.deal.info.summary?.dealType
                                                                    ? 'Target Return (p.a.)'
                                                                    : 'Interest Rate (p.a.)'}
                                                            </p>
                                                            <p className={valueClass}>
                                                                {parser(createInterestRate(d.deal))}
                                                            </p>
                                                        </div>
                                                    )}
                                                {/**Min Investment */}
                                                {isShowFields(d.deal.status) && createMinInvestment(d.deal) !== '-' && (
                                                    <div>
                                                        <p className={titleClass}>Min Investment</p>
                                                        <p className={valueClass}>
                                                            {parser(createMinInvestment(d.deal))}
                                                        </p>
                                                    </div>
                                                )}
                                                {/**Industry */}
                                                {dealTypes.equity === d.deal.info.summary?.dealType &&
                                                    isShowFields(d.deal.status) && (
                                                        <div>
                                                            <p className={titleClass}>Industry</p>
                                                            <p className={valueClass}>{d.issuer?.category || '-'}</p>
                                                        </div>
                                                    )}
                                            </div>
                                            {/** End Info */}
                                        </div>
                                    </div>
                                </div>
                                {/** End Detail container */}
                            </div>
                        </div>
                    );
                })}
            </Slider>
        </div>
    );
};

export default RecentlyClosedDesktop;
